<template>
  <div class="po-details-view">
    <div class="page-header">
      <div class="header-left">
        <button class="back-btn" @click="$router.push({ name: 'purchase-orders' })">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path d="M19 12H5M12 19l-7-7 7-7" />
          </svg>
        </button>
        <div class="header-info">
          <h1 class="page-heading">Détails de la commande</h1>
          <p class="page-subtitle">Consultez les informations complètes</p>
        </div>
      </div>
      <div v-if="po && po.id" class="header-actions">
        <button class="action-btn secondary" @click="printPurchaseOrder">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <polyline points="6 9 6 2 18 2 18 9"></polyline>
            <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"></path>
            <rect x="6" y="14" width="12" height="8"></rect>
          </svg>
          Imprimer
        </button>
        <button class="action-btn danger" @click="showCancelModal = true" :disabled="po.status === 'cancelled'">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="15" y1="9" x2="9" y2="15"></line>
            <line x1="9" y1="9" x2="15" y2="15"></line>
          </svg>
          Annuler
        </button>
      </div>
    </div>

    <div v-if="loading" class="loading-container">
      <div class="spinner"></div>
      <p>Chargement des détails...</p>
    </div>

    <div v-else-if="!po || !po.id" class="empty-state">
      <div class="empty-icon">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <circle cx="12" cy="12" r="10"></circle>
          <line x1="12" y1="8" x2="12" y2="12"></line>
          <line x1="12" y1="16" x2="12.01" y2="16"></line>
        </svg>
      </div>
      <h3>Commande introuvable</h3>
      <p>Cette commande n'existe pas ou a été supprimée</p>
      <button class="back-to-list-btn" @click="$router.push({ name: 'purchase-orders' })">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <path d="M19 12H5M12 19l-7-7 7-7" />
        </svg>
        Retour à la liste
      </button>
    </div>

    <div v-else class="content-layout">
      <div class="main-content">
        <div class="card order-header-card">
          <div class="order-header">
            <div class="order-info">
              <div class="reference-section">
                <div class="reference-badge">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                    <polyline points="14 2 14 8 20 8"></polyline>
                  </svg>
                </div>
                <div class="reference-info">
                  <h2 class="reference-number">{{ po.reference }}</h2>
                  <div class="date-info">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                      <line x1="16" y1="2" x2="16" y2="6"></line>
                      <line x1="8" y1="2" x2="8" y2="6"></line>
                      <line x1="3" y1="10" x2="21" y2="10"></line>
                    </svg>
                    <span>{{ formatDate(po.order_date) }}</span>
                  </div>
                </div>
              </div>
              <div class="status-section">
                <span class="status-label">Statut</span>
                <span class="status-badge" :class="getStatusClass(po.status)">
                  <span class="status-dot"></span>
                  {{ getStatusLabel(po.status) }}
                </span>
                <div v-if="po.has_invoice !== undefined" class="invoice-status">
                  <span class="invoice-badge" :class="po.has_invoice ? 'invoiced' : 'not-invoiced'">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                      <polyline points="14 2 14 8 20 8"></polyline>
                      <line x1="16" y1="13" x2="8" y2="13"></line>
                      <line x1="16" y1="17" x2="8" y2="17"></line>
                      <polyline points="10 9 9 9 8 9"></polyline>
                    </svg>
                    {{ po.has_invoice ? 'Facturée' : 'Non facturée' }}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div class="order-details-grid">
            <div class="detail-card supplier-card">
              <div class="detail-header">
                <div class="detail-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                    <circle cx="12" cy="7" r="4"></circle>
                  </svg>
                </div>
                <span class="detail-label">Fournisseur</span>
              </div>
              <div class="detail-content">
                <div class="supplier-avatar">
                  {{ getInitials(po.supplier?.name) }}
                </div>
                <div class="supplier-info">
                  <div class="supplier-name">{{ po.supplier?.name || '-' }}</div>
                  <div v-if="po.supplier?.phone || po.supplier?.email" class="supplier-meta">
                    <span v-if="po.supplier?.phone">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                        <path
                          d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z">
                        </path>
                      </svg>
                      {{ po.supplier.phone }}
                    </span>
                    <span v-if="po.supplier?.email">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                        <polyline points="22,6 12,13 2,6"></polyline>
                      </svg>
                      {{ po.supplier.email }}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div class="detail-card user-card">
              <div class="detail-header">
                <div class="detail-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                    <circle cx="9" cy="7" r="4"></circle>
                    <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                    <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                  </svg>
                </div>
                <span class="detail-label">Créée par</span>
              </div>
              <div class="detail-content">
                <div class="user-avatar">
                  {{ getInitials(po.user?.name) }}
                </div>
                <div class="user-info">
                  <div class="user-name">{{ po.user?.name || '-' }}</div>
                  <div class="user-date">{{ formatDateTime(po.created_at) }}</div>
                </div>
              </div>
            </div>

            <div v-if="po.notes" class="detail-card notes-card full-width">
              <div class="detail-header">
                <div class="detail-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                  </svg>
                </div>
                <span class="detail-label">Remarques</span>
              </div>
              <div class="notes-content">
                {{ po.notes }}
              </div>
            </div>
          </div>
        </div>

        <div class="card items-card">
          <div class="card-header">
            <div class="header-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"></path>
                <line x1="7" y1="7" x2="7.01" y2="7"></line>
              </svg>
            </div>
            <div class="header-text">
              <h3>Articles de la commande</h3>
              <p>{{ po.items?.length || 0 }} article{{ (po.items?.length || 0) > 1 ? 's' : '' }}</p>
            </div>
          </div>

          <div class="items-table-wrapper">
            <table class="items-table">
              <thead>
                <tr>
                  <th class="col-product">
                    <div class="th-content">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                        <circle cx="9" cy="21" r="1"></circle>
                        <circle cx="20" cy="21" r="1"></circle>
                        <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
                      </svg>
                      Produit
                    </div>
                  </th>
                  <th class="col-quantity">
                    <div class="th-content">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                        <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                        <circle cx="8.5" cy="7" r="4"></circle>
                        <polyline points="17 11 19 13 23 9"></polyline>
                      </svg>
                      Qté
                    </div>
                  </th>
                  <th class="col-price">
                    <div class="th-content">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                        <line x1="12" y1="1" x2="12" y2="23"></line>
                        <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                      </svg>
                      Prix achat
                    </div>
                  </th>
                  <th class="col-price">
                    <div class="th-content">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                        <line x1="12" y1="1" x2="12" y2="23"></line>
                        <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                      </svg>
                      Prix public
                    </div>
                  </th>
                  <th class="col-discount">
                    <div class="th-content">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                        <circle cx="12" cy="12" r="10"></circle>
                        <line x1="12" y1="8" x2="12" y2="16"></line>
                        <line x1="8" y1="12" x2="16" y2="12"></line>
                      </svg>
                      Remise
                    </div>
                  </th>
                  <th class="col-total">
                    <div class="th-content">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                        <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline>
                      </svg>
                      Total
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="item in po.items || []" :key="item.id" class="item-row">
                  <td class="col-product">
                    <div class="product-info">
                      <div class="product-icon">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                          <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z">
                          </path>
                          <line x1="7" y1="7" x2="7.01" y2="7"></line>
                        </svg>
                      </div>
                      <div class="product-details">
                        <div class="product-name">{{ item.product?.name || ('#' + item.product_id) }}</div>
                        <div v-if="item.product?.reference" class="product-ref">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                            <polyline points="14 2 14 8 20 8"></polyline>
                          </svg>
                          Réf: {{ item.product.reference }}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td class="col-quantity">
                    <div class="quantity-badge">{{ item.quantity }}</div>
                  </td>
                  <td class="col-price">
                    <div class="price-value">{{ formatCurrency(item.purchase_price) }}</div>
                  </td>
                  <td class="col-price">
                    <div class="price-value">{{ formatCurrency(item.sale_price) }}</div>
                  </td>
                  <td class="col-discount">
                    <div class="discount-value" v-if="item.discount > 0">-{{ formatCurrency(item.discount) }}</div>
                    <div class="no-discount" v-else>-</div>
                  </td>
                  <td class="col-total">
                    <div class="total-value">{{ formatCurrency(item.total_amount) }}</div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div class="sidebar">
        <div class="card summary-card">
          <div class="summary-header">
            <div class="summary-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path d="M9 11l3 3L22 4"></path>
                <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"></path>
              </svg>
            </div>
            <h3>Résumé</h3>
          </div>

          <div class="summary-content">
            <div class="summary-item">
              <div class="summary-label">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"></path>
                  <line x1="7" y1="7" x2="7.01" y2="7"></line>
                </svg>
                Articles
              </div>
              <div class="summary-value">{{ po.items?.length || 0 }}</div>
            </div>

            <div class="summary-divider"></div>

            <div class="summary-item highlight">
              <div class="summary-label">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <circle cx="9" cy="21" r="1"></circle>
                  <circle cx="20" cy="21" r="1"></circle>
                  <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
                </svg>
                Total achat
              </div>
              <div class="summary-value primary">{{ formatCurrency(totalPurchase) }}</div>
            </div>

            <div class="summary-item">
              <div class="summary-label">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <line x1="12" y1="1" x2="12" y2="23"></line>
                  <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                </svg>
                Total public
              </div>
              <div class="summary-value">{{ formatCurrency(totalPublic) }}</div>
            </div>

            <div class="summary-divider"></div>

            <div class="summary-item margin">
              <div class="summary-label">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline>
                </svg>
                Marge estimée
              </div>
              <div class="summary-value success">{{ formatCurrency(estimatedMargin) }}</div>
            </div>

            <div v-if="totalPublic > 0" class="margin-badge" :class="marginClass">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"></polyline>
                <polyline points="17 6 23 6 23 12"></polyline>
              </svg>
              {{ marginPercent }}% de marge
            </div>
          </div>
        </div>

        <div class="card timeline-card">
          <div class="timeline-header">
            <div class="timeline-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <circle cx="12" cy="12" r="10"></circle>
                <polyline points="12 6 12 12 16 14"></polyline>
              </svg>
            </div>
            <h3>Chronologie</h3>
          </div>

          <div class="timeline-content">
            <div class="timeline-item">
              <div class="timeline-dot created"></div>
              <div class="timeline-info">
                <div class="timeline-label">Commande créée</div>
                <div class="timeline-date">{{ formatDateTime(po.created_at) }}</div>
                <div class="timeline-user">{{ po.user?.name }}</div>
              </div>
            </div>

            <div v-if="po.updated_at && po.updated_at !== po.created_at" class="timeline-item">
              <div class="timeline-dot updated"></div>
              <div class="timeline-info">
                <div class="timeline-label">Dernière modification</div>
                <div class="timeline-date">{{ formatDateTime(po.updated_at) }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <transition name="modal">
      <div v-if="showCancelModal" class="modal-overlay" @click="showCancelModal = false">
        <div class="modal-content" @click.stop>
          <div class="modal-header warning">
            <div class="modal-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z">
                </path>
                <line x1="12" y1="9" x2="12" y2="13"></line>
                <line x1="12" y1="17" x2="12.01" y2="17"></line>
              </svg>
            </div>
            <h3>Annuler cette commande ?</h3>
          </div>
          <div class="modal-body">
            <p>Cette action marquera la commande comme annulée. Êtes-vous sûr de vouloir continuer ?</p>
          </div>
          <div class="modal-actions">
            <button class="modal-btn secondary" @click="showCancelModal = false">
              Non, garder
            </button>
            <button class="modal-btn danger" @click="confirmCancel">
              Oui, annuler
            </button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { defineComponent, ref, computed, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import purchaseOrderService from '@/services/purchaseOrderService'
import { useToastStore } from '@/store/modules/toastStore'

export default defineComponent({
  name: 'PurchaseOrderDetailsView',
  setup() {
    const route = useRoute()
    const toastStore = useToastStore()

    const po = ref(null)
    const loading = ref(true)
    const showCancelModal = ref(false)
    const showPrintOptions = ref(false)

    const loadDetails = async () => {
      loading.value = true
      try {
        const id = route.params.id
        const res = await purchaseOrderService.getPurchaseOrder(id, {})
        po.value = res.data?.data || res.data || {}
        po.value.items = po.value.items || po.value.purchase_order_items || po.value.sale_items || []
      } catch (e) {
        console.error('Error loading order:', e)
        po.value = null
        toastStore.addToast({
          type: 'error',
          message: 'Erreur lors du chargement de la commande',
          duration: 3000
        })
      } finally {
        loading.value = false
      }
    }

    const totalPurchase = computed(() => {
      const items = po.value?.items || []
      return items.reduce((s, it) => {
        return s + (Number(it.total_amount) || (Number(it.quantity) * Number(it.purchase_price) - (Number(it.discount) || 0)))
      }, 0)
    })

    const totalPublic = computed(() => {
      const items = po.value?.items || []
      return items.reduce((s, it) => {
        return s + ((Number(it.quantity) || 0) * (Number(it.sale_price) || 0))
      }, 0)
    })

    const estimatedMargin = computed(() => {
      return totalPublic.value - totalPurchase.value
    })

    const marginPercent = computed(() => {
      if (totalPublic.value === 0) return 0
      return ((estimatedMargin.value / totalPublic.value) * 100).toFixed(1)
    })

    const marginClass = computed(() => {
      const margin = parseFloat(marginPercent.value)
      if (margin >= 30) return 'high'
      if (margin >= 15) return 'medium'
      return 'low'
    })

    const getStatusLabel = (status) => {
      const map = { active: 'Active', inactive: 'Inactive', cancelled: 'Annulée' }
      return map[status] || status
    }

    const getStatusClass = (status) => {
      const map = { active: 'status-active', inactive: 'status-inactive', cancelled: 'status-cancelled' }
      return map[status] || 'status-inactive'
    }

    const formatCurrency = (amount) => {
      return new Intl.NumberFormat('fr-FR', {
        style: 'decimal',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(amount || 0) + ' FCFA'
    }

    const formatDate = (dateString) => {
      if (!dateString) return '-'
      const d = new Date(dateString)
      return d.toLocaleDateString('fr-FR', {
        year: 'numeric',
        month: 'long',
        day: '2-digit'
      })
    }

    const formatDateTime = (dateString) => {
      if (!dateString) return '-'
      const d = new Date(dateString)
      return d.toLocaleDateString('fr-FR', {
        year: 'numeric',
        month: 'short',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      })
    }

    const getInitials = (name) => {
      if (!name) return '?'
      const parts = name.trim().split(' ')
      if (parts.length >= 2) {
        return (parts[0][0] + parts[1][0]).toUpperCase()
      }
      return name.substring(0, 2).toUpperCase()
    }

    const confirmCancel = async () => {
      try {
        // Implement cancel logic here
        toastStore.addToast({
          type: 'success',
          message: 'Commande annulée avec succès',
          duration: 3000
        })
        showCancelModal.value = false
        loadDetails()
      } catch (e) {
        toastStore.addToast({
          type: 'error',
          message: 'Erreur lors de l\'annulation',
          duration: 3000
        })
      }
    }

    const printPurchaseOrder = async () => {
      try {
        await purchaseOrderService.printPurchaseOrder(po.value.id);
      } catch (error) {
        console.error('Erreur impression commande fournisseur:', error);
        toastStore.addToast({
          type: 'error',
          message: 'Erreur lors de l\'impression de la commande fournisseur',
          duration: 3000
        })
      }
    }

    onMounted(loadDetails)

    return {
      po,
      loading,
      totalPurchase,
      totalPublic,
      estimatedMargin,
      marginPercent,
      marginClass,
      showCancelModal,
      showPrintOptions,
      printPurchaseOrder,
      getStatusLabel,
      getStatusClass,
      formatCurrency,
      formatDate,
      formatDateTime,
      getInitials,
      confirmCancel
    }
  }
})
</script>

<style scoped>
.po-details-view {
  padding: 0 0 24px 0;
  max-width: 1600px;
  margin: 0 auto;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  gap: 16px;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 16px;
  flex: 1;
}

.back-btn {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  border: 1.5px solid #e2e8f0;
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
}

.back-btn:hover {
  background: #f8fafc;
  border-color: #0A5C6B;
}

.back-btn svg {
  width: 20px;
  height: 20px;
  stroke: #475569;
  stroke-width: 2;
}

.header-info {
  flex: 1;
}

.page-heading {
  font-size: 24px;
  font-weight: 700;
  color: #0f172a;
  margin: 0 0 4px 0;
  letter-spacing: -0.5px;
}

.page-subtitle {
  font-size: 14px;
  color: #64748b;
  margin: 0;
}

.header-actions {
  display: flex;
  gap: 8px;
}

.action-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  border: none;
  transition: all 0.2s;
}

.action-btn svg {
  width: 18px;
  height: 18px;
  stroke-width: 2;
}

.action-btn.secondary {
  background: white;
  color: #475569;
  border: 1.5px solid #e2e8f0;
}

.action-btn.secondary:hover {
  background: #f8fafc;
  border-color: #cbd5e1;
}

.action-btn.danger {
  background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
  color: white;
}

.action-btn.danger:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(220, 38, 38, 0.3);
}

.action-btn.danger:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 80px 20px;
  gap: 16px;
}

.spinner {
  width: 48px;
  height: 48px;
  border: 4px solid rgba(10, 92, 107, 0.1);
  border-top-color: #0A5C6B;
  border-radius: 50%;
  animation: spin 0.8s cubic-bezier(0.68, -0.55, 0.265, 1.55) infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.loading-container p {
  font-size: 14px;
  color: #64748b;
  margin: 0;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  text-align: center;
  gap: 16px;
}

.empty-icon {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: linear-gradient(135deg, #ffebee 0%, #ffcdd2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
}

.empty-icon svg {
  width: 40px;
  height: 40px;
  stroke: #c62828;
  stroke-width: 2;
}

.empty-state h3 {
  font-size: 18px;
  font-weight: 700;
  color: #0f172a;
  margin: 0;
}

.empty-state p {
  font-size: 14px;
  color: #64748b;
  margin: 0;
}

.back-to-list-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: linear-gradient(135deg, #0A5C6B 0%, #0e7689 100%);
  color: white;
  border: none;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  margin-top: 8px;
}

.back-to-list-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 16px rgba(10, 92, 107, 0.3);
}

.back-to-list-btn svg {
  width: 18px;
  height: 18px;
  stroke-width: 2;
}

.content-layout {
  display: grid;
  grid-template-columns: 1fr 380px;
  gap: 24px;
}

.main-content {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.card {
  background: white;
  border: 1.5px solid #e2e8f0;
  border-radius: 12px;
  overflow: hidden;
}

.order-header-card {
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
}

.order-header {
  padding: 24px;
  border-bottom: 2px solid #e2e8f0;
}

.order-info {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 24px;
  flex-wrap: wrap;
}

.reference-section {
  display: flex;
  align-items: center;
  gap: 16px;
}

.reference-badge {
  width: 56px;
  height: 56px;
  border-radius: 14px;
  background: linear-gradient(135deg, #0A5C6B 0%, #0e7689 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 4px 12px rgba(10, 92, 107, 0.3);
}

.reference-badge svg {
  width: 28px;
  height: 28px;
  stroke: white;
  stroke-width: 2;
}

.reference-info {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.reference-number {
  font-size: 22px;
  font-weight: 800;
  color: #0f172a;
  margin: 0;
  letter-spacing: -0.5px;
}

.date-info {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #64748b;
  font-size: 14px;
}

.date-info svg {
  width: 16px;
  height: 16px;
  stroke-width: 2;
}

.status-section {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
}

.invoice-status {
  margin-top: 4px;
}

.invoice-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
}

.invoice-badge svg {
  width: 14px;
  height: 14px;
  stroke-width: 2;
}

.invoice-badge.invoiced {
  background: linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%);
  color: #2e7d32;
}

.invoice-badge.not-invoiced {
  background: linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%);
  color: #e65100;
}

.status-label {
  font-size: 12px;
  color: #64748b;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 700;
}

.status-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
}

.status-badge.status-active {
  background: linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%);
  color: #2e7d32;
}

.status-badge.status-active .status-dot {
  background: #2e7d32;
  box-shadow: 0 0 0 3px rgba(46, 125, 50, 0.2);
}

.status-badge.status-inactive {
  background: linear-gradient(135deg, #f5f5f5 0%, #e0e0e0 100%);
  color: #616161;
}

.status-badge.status-inactive .status-dot {
  background: #616161;
  box-shadow: 0 0 0 3px rgba(97, 97, 97, 0.2);
}

.status-badge.status-cancelled {
  background: linear-gradient(135deg, #ffebee 0%, #ffcdd2 100%);
  color: #c62828;
}

.status-badge.status-cancelled .status-dot {
  background: #c62828;
  box-shadow: 0 0 0 3px rgba(198, 40, 40, 0.2);
}

.order-details-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  padding: 24px;
}

.detail-card {
  background: white;
  border: 1.5px solid #e2e8f0;
  border-radius: 12px;
  padding: 20px;
  transition: all 0.2s;
}

.detail-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);
}

.detail-card.full-width {
  grid-column: 1 / -1;
}

.detail-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
}

.detail-icon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.supplier-card .detail-icon {
  background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%);
}

.supplier-card .detail-icon svg {
  width: 20px;
  height: 20px;
  stroke: #1976d2;
  stroke-width: 2;
}

.user-card .detail-icon {
  background: linear-gradient(135deg, #f3e5f5 0%, #e1bee7 100%);
}

.user-card .detail-icon svg {
  width: 20px;
  height: 20px;
  stroke: #7b1fa2;
  stroke-width: 2;
}

.notes-card .detail-icon {
  background: linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%);
}

.notes-card .detail-icon svg {
  width: 20px;
  height: 20px;
  stroke: #e65100;
  stroke-width: 2;
}

.detail-label {
  font-size: 13px;
  font-weight: 700;
  color: #475569;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.detail-content {
  display: flex;
  align-items: center;
  gap: 16px;
}

.supplier-avatar,
.user-avatar {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: linear-gradient(135deg, #0A5C6B 0%, #0e7689 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  font-weight: 700;
  flex-shrink: 0;
}

.user-avatar {
  background: linear-gradient(135deg, #9c27b0 0%, #ba68c8 100%);
}

.supplier-info,
.user-info {
  flex: 1;
}

.supplier-name,
.user-name {
  font-size: 16px;
  font-weight: 700;
  color: #0f172a;
  margin-bottom: 6px;
}

.supplier-meta {
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 13px;
  color: #64748b;
}

.supplier-meta span {
  display: flex;
  align-items: center;
  gap: 6px;
}

.supplier-meta svg {
  width: 14px;
  height: 14px;
  stroke-width: 2;
}

.user-date {
  font-size: 13px;
  color: #64748b;
}

.notes-content {
  font-size: 14px;
  color: #475569;
  line-height: 1.6;
  background: #f8fafc;
  padding: 12px;
  border-radius: 8px;
}

.card-header {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 20px 24px;
  border-bottom: 2px solid #e2e8f0;
  background: linear-gradient(135deg, #fafbfc 0%, #f8fafc 100%);
}

.header-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: linear-gradient(135deg, #f3e5f5 0%, #e1bee7 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.header-icon svg {
  width: 24px;
  height: 24px;
  stroke: #7b1fa2;
  stroke-width: 2;
}

.header-text {
  flex: 1;
}

.header-text h3 {
  font-size: 18px;
  font-weight: 700;
  color: #0f172a;
  margin: 0 0 4px 0;
}

.header-text p {
  font-size: 13px;
  color: #64748b;
  margin: 0;
}

.items-table-wrapper {
  overflow-x: auto;
}

.items-table {
  width: 100%;
  border-collapse: collapse;
}

.items-table thead {
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border-bottom: 2px solid #e2e8f0;
}

.items-table th {
  padding: 16px 20px;
  text-align: left;
  font-size: 12px;
  font-weight: 700;
  color: #475569;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.th-content {
  display: flex;
  align-items: center;
  gap: 8px;
}

.th-content svg {
  width: 16px;
  height: 16px;
  stroke: #94a3b8;
  stroke-width: 2;
}

.items-table td {
  padding: 16px 20px;
  border-bottom: 1px solid #f1f5f9;
  font-size: 14px;
}

.item-row:hover {
  background: linear-gradient(135deg, #fafbfc 0%, #f8fafc 100%);
}

.item-row:last-child td {
  border-bottom: none;
}

.col-product {
  min-width: 300px;
}

.col-quantity,
.col-discount {
  text-align: center;
  width: 100px;
}

.col-price,
.col-total {
  text-align: right;
  width: 140px;
}

.product-info {
  display: flex;
  align-items: center;
  gap: 12px;
}

.product-icon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.product-icon svg {
  width: 20px;
  height: 20px;
  stroke: #0284c7;
  stroke-width: 2;
}

.product-details {
  flex: 1;
}

.product-name {
  font-weight: 700;
  color: #0f172a;
  margin-bottom: 4px;
}

.product-ref {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #64748b;
}

.product-ref svg {
  width: 12px;
  height: 12px;
  stroke-width: 2;
}

.quantity-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 6px 12px;
  background: linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%);
  color: #2e7d32;
  border-radius: 8px;
  font-weight: 700;
  font-size: 14px;
}

.price-value,
.total-value {
  font-weight: 700;
  color: #0f172a;
}

.total-value {
  color: #0A5C6B;
  font-size: 15px;
}

.discount-value {
  color: #dc2626;
  font-weight: 700;
}

.no-discount {
  color: #94a3b8;
}

.sidebar {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.summary-card,
.timeline-card {
  position: sticky;
  top: 20px;
}

.summary-card {
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
  border: 2px solid #e2e8f0;
}

.summary-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 20px 24px;
  border-bottom: 2px solid #e2e8f0;
}

.summary-icon {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: linear-gradient(135deg, #0A5C6B 0%, #0e7689 100%);
  display: flex;
  align-items: center;
  justify-content: center;
}

.summary-icon svg {
  width: 22px;
  height: 22px;
  stroke: white;
  stroke-width: 2;
}

.summary-header h3 {
  font-size: 18px;
  font-weight: 700;
  color: #0f172a;
  margin: 0;
}

.summary-content {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 20px 24px;
}

.summary-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px;
  background: white;
  border-radius: 8px;
}

.summary-item.highlight {
  background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
  border: 1.5px solid #bae6fd;
}

.summary-item.margin {
  background: linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%);
  border: 1.5px solid #a5d6a7;
}

.summary-label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #64748b;
  font-weight: 600;
}

.summary-label svg {
  width: 16px;
  height: 16px;
  stroke: #94a3b8;
  stroke-width: 2;
}

.summary-value {
  font-size: 16px;
  font-weight: 700;
  color: #0f172a;
}

.summary-value.primary {
  font-size: 18px;
  color: #0A5C6B;
}

.summary-value.success {
  color: #2e7d32;
}

.summary-divider {
  height: 1px;
  background: linear-gradient(90deg, transparent, #e2e8f0, transparent);
}

.margin-badge {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 16px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 700;
  margin-top: 8px;
}

.margin-badge svg {
  width: 18px;
  height: 18px;
  stroke-width: 2.5;
}

.margin-badge.high {
  background: linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%);
  color: #2e7d32;
}

.margin-badge.medium {
  background: linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%);
  color: #e65100;
}

.margin-badge.low {
  background: linear-gradient(135deg, #ffebee 0%, #ffcdd2 100%);
  color: #c62828;
}

.timeline-card {
  background: white;
}

.timeline-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 20px 24px;
  border-bottom: 2px solid #e2e8f0;
}

.timeline-icon {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: linear-gradient(135deg, #f3e5f5 0%, #e1bee7 100%);
  display: flex;
  align-items: center;
  justify-content: center;
}

.timeline-icon svg {
  width: 22px;
  height: 22px;
  stroke: #7b1fa2;
  stroke-width: 2;
}

.timeline-header h3 {
  font-size: 18px;
  font-weight: 700;
  color: #0f172a;
  margin: 0;
}

.timeline-content {
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.timeline-item {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  position: relative;
}

.timeline-item:not(:last-child)::after {
  content: '';
  position: absolute;
  left: 11px;
  top: 32px;
  bottom: -20px;
  width: 2px;
  background: linear-gradient(180deg, #e2e8f0 0%, transparent 100%);
}

.timeline-dot {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  z-index: 1;
}

.timeline-dot.created {
  background: linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%);
  box-shadow: 0 0 0 4px rgba(46, 125, 50, 0.1);
}

.timeline-dot.updated {
  background: linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%);
  box-shadow: 0 0 0 4px rgba(230, 81, 0, 0.1);
}

.timeline-info {
  flex: 1;
}

.timeline-label {
  font-size: 14px;
  font-weight: 700;
  color: #0f172a;
  margin-bottom: 4px;
}

.timeline-date {
  font-size: 13px;
  color: #64748b;
  margin-bottom: 2px;
}

.timeline-user {
  font-size: 12px;
  color: #94a3b8;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
}

.modal-content {
  background: white;
  border-radius: 16px;
  max-width: 480px;
  width: 100%;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  animation: modalSlideUp 0.3s ease-out;
}

@keyframes modalSlideUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.modal-header {
  padding: 24px 24px 20px;
  text-align: center;
}

.modal-icon {
  width: 64px;
  height: 64px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 16px;
}

.modal-header.warning .modal-icon {
  background: linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%);
}

.modal-header.warning .modal-icon svg {
  width: 32px;
  height: 32px;
  stroke: #e65100;
  stroke-width: 2;
}

.modal-header h3 {
  font-size: 20px;
  font-weight: 700;
  color: #0f172a;
  margin: 0;
}

.modal-body {
  padding: 0 24px 24px;
}

.modal-body p {
  font-size: 14px;
  color: #64748b;
  margin: 0;
  text-align: center;
  line-height: 1.6;
}

.modal-actions {
  display: flex;
  gap: 12px;
  padding: 0 24px 24px;
}

.modal-btn {
  flex: 1;
  padding: 12px 20px;
  border: none;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.modal-btn.secondary {
  background: #f1f5f9;
  color: #475569;
}

.modal-btn.secondary:hover {
  background: #e2e8f0;
}

.modal-btn.danger {
  background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
  color: white;
}

.modal-btn.danger:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(220, 38, 38, 0.3);
}

.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.3s;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

@media (max-width: 1280px) {
  .content-layout {
    grid-template-columns: 1fr 340px;
  }
}

@media (max-width: 1024px) {
  .content-layout {
    grid-template-columns: 1fr;
  }

  .summary-card,
  .timeline-card {
    position: static;
  }
}

@media (max-width: 768px) {
  .po-details-view {
    padding: 0 0 16px 0;
  }

  .page-header {
    flex-direction: column;
    align-items: stretch;
  }

  .header-left {
    flex-direction: column;
    align-items: stretch;
  }

  .back-btn {
    align-self: flex-start;
  }

  .header-actions {
    width: 100%;
  }

  .action-btn {
    flex: 1;
    justify-content: center;
  }

  .order-info {
    flex-direction: column;
  }

  .status-section {
    align-items: flex-start;
  }

  .order-details-grid {
    grid-template-columns: 1fr;
  }

  .items-table-wrapper {
    overflow-x: auto;
    margin: 0 -24px;
  }

  .items-table {
    min-width: 800px;
  }
}
</style>